import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Gem, Clock3, ShieldCheck, Hammer, Ruler, Truck } from 'lucide-react';
import Reveal from '../components/Reveal';
import CountUp from '../components/CountUp';
import heroFacets from '../assets/hero-facets.svg';
import workshopImg from '../assets/workshop.svg';
import glassPanel from '../assets/products/glass-panel.svg';
import aluminiumSliding from '../assets/products/aluminium-sliding.svg';
import pictureFraming from '../assets/products/picture-framing.svg';
import namePlates from '../assets/products/name-plates.svg';
import ledMirrors from '../assets/products/led-mirrors.svg';
import wallDecor from '../assets/products/wall-decor.svg';
import stainedGlass from '../assets/products/stained-glass.svg';
import mirrorsEtching from '../assets/products/mirrors-etching.svg';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'The Best Framing Under One Roof',
      description: 'Our heart and speciality — custom picture framing, art framing and name plates, crafted by hand.',
      eyebrow: 'Pune\u2019s Framing Specialists'
    },
    {
      title: 'Framing for Every Memory',
      description: 'From family portraits to certificates and fine art, framed with care and precision.',
      eyebrow: 'Our signature craft'
    },
    {
      title: 'All Types of Glass Work',
      description: 'Panels, shelves, mirrors, stained glass and etching — complementing our framing expertise.',
      eyebrow: 'Since generations, in Pune'
    },
    {
      title: 'Aluminium Slidings',
      description: 'Smooth-gliding, weatherproof aluminium windows and doors, built to last.',
      eyebrow: 'Modern & durable'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const services = [
    { name: 'Picture Framing', img: pictureFraming, description: 'Our speciality — the best framing under one roof, for photographs and artwork alike.', badge: 'Speciality' },
    { name: 'Name Plates', img: namePlates, description: 'Custom-engraved name plates in glass and metal finishes.', badge: 'Speciality' },
    { name: 'Glass Panels & Shelves', img: glassPanel, description: 'Custom-cut glass panels, shelves and toughened glass for home and office.' },
    { name: 'Aluminium Slidings', img: aluminiumSliding, description: 'High-quality aluminium sliding windows and doors, fitted to measure.' },
    { name: 'Stained Glass', img: stainedGlass, description: 'Traditional leaded and stained glass panels with lasting colour.' },
    { name: 'Mirrors & Etching', img: mirrorsEtching, description: 'Frosted etching and mirror work for a refined, modern finish.' },
    { name: 'LED Mirrors', img: ledMirrors, description: 'Contemporary LED-lit mirrors that bring warmth to any room.' },
    { name: 'Decorative Wall Art', img: wallDecor, description: 'Sunburst mirrors and glass wall pieces to complete your décor.' }
  ];

  const process = [
    { icon: <Ruler size={26} />, title: 'Site Visit & Measurement', text: 'We visit, measure precisely, and understand your space.' },
    { icon: <Hammer size={26} />, title: 'Cutting & Crafting', text: 'Glass and frames are cut, shaped and finished by hand.' },
    { icon: <ShieldCheck size={26} />, title: 'Quality Check', text: 'Every edge and fitting is checked before it leaves the workshop.' },
    { icon: <Truck size={26} />, title: 'Fitting & Delivery', text: 'Careful installation at your home or office, right on schedule.' }
  ];

  return (
    <div className="pt-16 bg-cream-50">
      {/* Hero Slider */}
      <div className="relative h-[620px] overflow-hidden">
        <img src={heroFacets} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-cream-50/35"></div>

        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center justify-center ${
              index === currentSlide
                ? 'opacity-100 translate-x-0'
                : index < currentSlide
                ? 'opacity-0 -translate-x-10'
                : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="text-center px-4 z-10 max-w-3xl">
              <span className="inline-block mb-4 text-gold-700 tracking-[0.25em] uppercase text-xs font-semibold bg-gold-100/80 border border-gold-300 rounded-full px-4 py-1.5 animate-fadeInUp">
                {slide.eyebrow}
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 text-ink-700 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                {slide.title}
              </h1>
              <p className="text-lg md:text-xl mb-8 text-ink-500 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                {slide.description}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 animate-fadeInUp" style={{ animationDelay: '0.35s' }}>
                <Link
                  to="/contact"
                  className="glass-sheen inline-flex items-center bg-gradient-to-r from-crimson-500 to-crimson-600 hover:from-crimson-600 hover:to-crimson-700 text-white px-8 py-4 rounded-lg text-base font-semibold shadow-lg shadow-crimson-500/30 transition-all duration-300 transform hover:scale-105"
                >
                  Get a Free Quote
                  <ArrowRight className="ml-2" size={18} />
                </Link>
                <a
                  href="tel:+919359370652"
                  className="inline-flex items-center bg-white/80 hover:bg-white text-ink-700 border border-gold-300 px-8 py-4 rounded-lg text-base font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  Call: +91 93593 70652
                </a>
              </div>
            </div>
          </div>
        ))}

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-crimson-600 hover:text-white text-ink-600 p-3 rounded-full transition-all duration-300 transform hover:scale-110 z-20 shadow-md"
          aria-label="Previous slide"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-crimson-600 hover:text-white text-ink-600 p-3 rounded-full transition-all duration-300 transform hover:scale-110 z-20 shadow-md"
          aria-label="Next slide"
        >
          <ChevronRight size={28} />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-crimson-600 w-10 shadow' : 'bg-white/70 w-2.5 hover:bg-white'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Welcome / About Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal animation="left">
            <span className="text-crimson-600 font-semibold tracking-widest uppercase text-xs">Welcome to India Glass Works &amp; Frames</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-ink-700 mt-3 mb-6">
              Pune&rsquo;s framing specialists, since generations
            </h2>
            <p className="text-ink-500 text-lg leading-relaxed mb-4">
              Framing is at the heart of everything we do. Located on Kolsa Lane off M. G. Road, we are known
              as <span className="font-semibold text-ink-600">the best framing under one roof</span> — for
              photographs, artwork, certificates and custom name plates, all cut and finished by hand.
            </p>
            <p className="text-ink-500 text-lg leading-relaxed mb-8">
              Alongside our framing work, we're also dealers in all types of glass — panels, shelves,
              stained glass, etching, mirrors — and aluminium sliding, so your entire glass and framing
              needs are handled under one roof.
            </p>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="font-display text-3xl font-bold text-crimson-600">Framing</div>
                <div className="text-ink-500 text-sm">Our speciality</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold text-crimson-600">
                  <CountUp value={8} suffix="+" />
                </div>
                <div className="text-ink-500 text-sm">Glass &amp; frame services</div>
              </div>
              <div>
                <div className="font-display text-3xl font-bold text-crimson-600">Mon-Sat</div>
                <div className="text-ink-500 text-sm">9 AM – 7 PM</div>
              </div>
            </div>
          </Reveal>
          <Reveal animation="right" delay={120} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold-200/60 to-crimson-100/60 rounded-3xl blur-xl"></div>
            <img
              src={workshopImg}
              alt="Illustration of the India Glass Works workshop with glass panels and framing tools"
              className="relative rounded-2xl shadow-xl w-full animate-float"
            />
          </Reveal>
        </div>
      </div>

      {/* Services Section */}
      <div id="services" className="bg-cream-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-crimson-600 font-semibold tracking-widest uppercase text-xs">What We Do</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-ink-700 mt-3">Our Products &amp; Services</h2>
            <p className="text-ink-500 max-w-2xl mx-auto mt-4">
              Framing is where we started and where we shine — backed by a full range of glass and mirror work.
            </p>
            <div className="ornament-divider mt-6"><Gem size={16} className="text-gold-500" /></div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Reveal key={index} delay={(index % 4) * 90}>
                <div className="frame-corners relative bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gold-100">
                  {service.badge && (
                    <span className="absolute top-3 right-3 z-10 bg-gold-400 text-ink-700 text-[10px] font-bold tracking-wide uppercase px-2.5 py-1 rounded-full shadow">
                      {service.badge}
                    </span>
                  )}
                  <div className="overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.name}
                      className="w-full h-40 object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold mb-2 text-ink-700">
                      {service.name}
                    </h3>
                    <p className="text-ink-500 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-crimson-600 hover:text-crimson-700 font-semibold border-b-2 border-crimson-200 hover:border-crimson-500 pb-1 transition-all"
            >
              View Full Product Details
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-crimson-600 font-semibold tracking-widest uppercase text-xs">How We Work</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-ink-700 mt-3">From Measurement to Fitting</h2>
            <div className="ornament-divider mt-6"><Gem size={16} className="text-gold-500" /></div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <Reveal key={index} delay={index * 110} animation="scale" className="relative text-center px-4">
                <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-gradient-to-br from-crimson-500 to-crimson-600 text-white flex items-center justify-center shadow-lg shadow-crimson-500/30">
                  {step.icon}
                </div>
                <h3 className="font-display font-bold text-ink-700 mb-2">{step.title}</h3>
                <p className="text-ink-500 text-sm">{step.text}</p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-full h-px bg-gradient-to-r from-gold-300 to-transparent"></div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-gradient-to-br from-crimson-500 via-crimson-600 to-crimson-700 text-white py-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-gold-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-gold-300/20 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center mb-14">
            <span className="text-gold-200 font-semibold tracking-widest uppercase text-xs">Why India Glass Works</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Built on Craft &amp; Trust</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Reveal delay={0}>
              <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/10">
                <Gem className="mx-auto mb-4 text-gold-200" size={36} />
                <h3 className="font-display text-2xl font-bold mb-3">Quality Craftsmanship</h3>
                <p className="text-cream-100/90">Premium glass and materials, finished by experienced hands.</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/10">
                <Clock3 className="mx-auto mb-4 text-gold-200" size={36} />
                <h3 className="font-display text-2xl font-bold mb-3">Reliable Turnaround</h3>
                <p className="text-cream-100/90">Prompt measurement, fitting and delivery, without cutting corners.</p>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/10">
                <ShieldCheck className="mx-auto mb-4 text-gold-200" size={36} />
                <h3 className="font-display text-2xl font-bold mb-3">Trusted in Pune</h3>
                <p className="text-cream-100/90">A Camp, Pune address serving homes and offices for years.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-cream-100 py-16">
        <Reveal animation="scale" className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-ink-700">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg mb-8 text-ink-500">
            Visit our showroom on Kolsa Lane or contact us today for a free consultation and quote.
          </p>
          <Link
            to="/contact"
            className="glass-sheen inline-flex items-center bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-ink-700 px-10 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gold-500/30"
          >
            Contact Us Now
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default Home;
